export const T12IncomeStatement = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Income Analysis</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Understanding Trailing 12 Income Components
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Every dollar of income in your real estate Trailing 12 must be documented, verified, and categorized correctly. Here's what buyers scrutinize most carefully.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/d7a9b4984176eb9784bc7fe95d9f088e.jpg"
                alt="Detailed T12 income statement showing rental revenue breakdown"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-4-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Rental Income (Primary Revenue)</h3>
                  <p className="text-gray-700 mb-3">
                    This is your largest income source - the actual rent collected from tenants over 12 months. Critical distinction: use <strong>collected rent</strong>, not scheduled rent.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Scheduled Annual Rent:</span>
                      <span className="font-semibold text-gray-900">$360,000</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-red-600">Uncollected Rent:</span>
                      <span className="font-semibold text-red-600">-$12,000</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-gray-300 pt-2">
                      <span className="font-bold text-gray-900">Actual Collected Rent:</span>
                      <span className="font-bold text-teal-600">$348,000</span>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-gray-700"><strong>Red Flag:</strong> If scheduled rent significantly exceeds collected rent, buyers will question your tenant quality and collection practices.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-parking-box-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Other Income Sources</h3>
                  <p className="text-gray-700 mb-3">
                    Additional revenue streams that supplement rental income. These can add 3-8% to your total income.
                  </p>
                  <div className="space-y-2">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Parking Fees</span>
                        <span className="text-sm font-semibold text-gray-900">$50-150/space/month</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Laundry Income</span>
                        <span className="text-sm font-semibold text-gray-900">$15-30/unit/month</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Pet Rent/Fees</span>
                        <span className="text-sm font-semibold text-gray-900">$25-75/pet/month</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Storage Fees</span>
                        <span className="text-sm font-semibold text-gray-900">$25-100/unit/month</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Late Fees</span>
                        <span className="text-sm font-semibold text-gray-900">$50-100/occurrence</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Vacancy Loss: The Critical Deduction</h3>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <p className="text-gray-700 mb-4">
                Vacancy loss represents income you didn't collect because units were empty. This is one of the most scrutinized line items because it directly reflects property performance and management quality.
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-gray-200 mb-4">
                <h4 className="font-bold text-gray-900 mb-3">How to Calculate Vacancy Loss</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-600 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">Calculate potential gross income if 100% occupied</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">24 units × $1,200 × 12 = $345,600</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-600 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">Track actual collected rent</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">$328,320 collected</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-600 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">Calculate vacancy loss</p>
                      <p className="text-sm font-semibold text-red-600 mt-1">$345,600 - $328,320 = $17,280 (5% vacancy)</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                <p className="text-sm text-gray-700"><strong>Buyer Concern:</strong> Vacancy rates above 10% raise serious questions about property condition, location, management, or market demand.</p>
              </div>
            </div>
            
            <div>
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-4">Vacancy Rate Benchmarks</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <div className="font-semibold text-gray-900">0-5% Vacancy</div>
                      <div className="text-sm text-gray-600">Excellent Performance</div>
                    </div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <div className="font-semibold text-gray-900">5-8% Vacancy</div>
                      <div className="text-sm text-gray-600">Market Average</div>
                    </div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div>
                      <div className="font-semibold text-gray-900">8-12% Vacancy</div>
                      <div className="text-sm text-gray-600">Concerning - Needs Attention</div>
                    </div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div>
                      <div className="font-semibold text-gray-900">12%+ Vacancy</div>
                      <div className="text-sm text-gray-600">Major Red Flag</div>
                    </div>
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-gray-700 mb-2"><strong>Impact on Value:</strong></p>
                  <p className="text-sm text-gray-700">A property with 5% vacancy vs 15% vacancy can see a 20-30% difference in sale price, even if the units are identical.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-6 text-center">Income Verification: What Buyers Check</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bank-line text-2xl text-white"></i>
              </div>
              <h4 className="font-bold mb-3">Bank Statements</h4>
              <p className="text-sm text-gray-300">
                Buyers verify your T12 income against 12 months of bank deposits. Every dollar must match. Missing deposits or unexplained discrepancies kill deals instantly.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-2xl text-white"></i>
              </div>
              <h4 className="font-bold mb-3">Rent Roll</h4>
              <p className="text-sm text-gray-300">
                Current rent roll must match your T12 rental income. Buyers calculate: number of units × average rent × 12 months and compare to your T12 figures.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-2xl text-white"></i>
              </div>
              <h4 className="font-bold mb-3">Tax Returns</h4>
              <p className="text-sm text-gray-300">
                Your Schedule E (rental income) on tax returns should align with T12 income. Major discrepancies suggest either tax fraud or inflated T12 numbers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};